import { useState, useEffect } from 'react';
import { collection, onSnapshot, Timestamp, deleteDoc, doc, query, where, updateDoc, writeBatch, getDoc } from 'firebase/firestore';
import { db, auth, getImpersonationData } from '../lib/firebase';
import { CalendarEvent } from '../types';
import { isAfter, isBefore, isSameDay } from 'date-fns';

export const useEvents = () => {
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!auth.currentUser) {
      setEvents([]);
      setLoading(false);
      return;
    }

    // Check if impersonating - use target user's ID, otherwise use actual user's ID
    const impersonation = getImpersonationData();
    const userId = impersonation ? impersonation.targetUserId : auth.currentUser.uid;

    const eventsQuery = query(
      collection(db, 'events'),
      where('userId', '==', userId)
    );

    const unsubscribe = onSnapshot(
      eventsQuery,
      (snapshot) => {
        try {
          const eventsList = snapshot.docs.map(doc => {
            const data = doc.data();
            return {
              ...data,
              id: doc.id,
              date: data.date instanceof Timestamp ? 
                data.date.toDate() : 
                new Date(data.date),
              createdAt: data.createdAt?.toDate?.() || null,
              updatedAt: data.updatedAt?.toDate?.() || null,
              paidDate: data.paidDate?.toDate?.() || null
            } as CalendarEvent;
          });
          setEvents(eventsList);
          setLoading(false);
        } catch (err) {
          setError(err instanceof Error ? err : new Error('Failed to process events'));
          setLoading(false);
        }
      },
      (err) => {
        setError(err);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  const calculateBalanceAtDate = (date: Date): number => {
    if (!events) return 0;

    // Find the latest adjustment before or on the given date
    const latestAdjustment = events
      .filter(event =>
        event.type === 'adjustment' &&
        (isBefore(event.date, date) || isSameDay(event.date, date))
      )
      .sort((a, b) => b.date.getTime() - a.date.getTime())[0];

    // If there's an adjustment, only consider transactions after it
    const startDate = latestAdjustment ? latestAdjustment.date : new Date(0);
    const startingBalance = latestAdjustment ? latestAdjustment.amount : 0;

    // Get all relevant transactions (both paid and unpaid for projection)
    const relevantTransactions = events
      .filter(event => {
        const eventDate = event.date;
        return (isAfter(eventDate, startDate) || (event === latestAdjustment)) &&
               (isBefore(eventDate, date) || isSameDay(eventDate, date));
      })
      .sort((a, b) => a.date.getTime() - b.date.getTime());

    // Calculate running balance including all transactions
    return relevantTransactions.reduce((balance, event) => {
      if (event.type === 'adjustment') {
        return event.amount;
      }
      if (event.type === 'payday') {
        return balance + event.amount;
      }
      if (event.type === 'bill' || event.type === 'bucket') {
        return balance - event.amount;
      }
      return balance;
    }, 0);
  };

  const deleteEvent = async (eventId: string) => {
    try {
      const eventToDelete = events.find(e => e.id === eventId);
      if (!eventToDelete) return;

      // If this is a bucket event, also delete the bucket
      if (eventToDelete.type === 'bucket' && eventToDelete.bucketId) {
        await deleteDoc(doc(db, 'budgetBuckets', eventToDelete.bucketId));
      }

      const docRef = doc(db, 'events', eventId);
      await deleteDoc(docRef);
    } catch (err) {
      console.error('Error deleting event:', err);
    }
  };

  return { events, loading, error, deleteEvent, calculateBalanceAtDate };
};

export default useEvents;